import React, { useEffect, useState } from 'react';

import {
    styled,
    Typography,
    Box,
    Grid,
    FormControlLabel,
    FormControl,
    FormHelperText,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Chip, RadioGroup, Radio } from '@mui/material';

import { AdminPage, ControlBar, ControlBarHeader } from 'components/ui/layout';
import { Section, Header, Content } from 'components/ui/forms';
import { SubmitButton } from 'components/ui/buttons';
import { TextField } from 'components/ui/inputs';

import { useTextInput } from 'hooks/forms';
import { validateURL } from 'utility/validation';
import { createNotification } from './NotificationAPI';
import { useNotification } from 'context/notification';
import fetchExpresso from 'utility/fetchExpresso';

// This screen allows admins to send notifications to the inboxes of our hosts
// Currently only supports the ability to send to every host in the system
// We may add in the ability to target specific hosts in the future

export default function Notification(props) {
    const [loading, setLoading] = useState(false);

    const title = useTextInput({ maxLength: 200 });
    const url = useTextInput({ maxLength: 200 });
    // we dont have a useSelectInput yet so we can use state for the select
    const [selectHostInputError, setSelectHostInputError] = useState(null);

    const [recipientMode, setRecipientMode] = useState('ALL'); // ALL, SELECT

    // eslint-disable-next-line
    const [hosts, setHosts] = useState([]);

    const [selectedHosts, setSelectedHosts] = useState([]);

    const { notify } = useNotification();

    useEffect(() => {
        // we dont want to fetch for no reason so only if user select the mode select and also not alreadied fetch
        if (recipientMode === 'SELECT' && hosts.length === 0) {
            fetchExpresso('/apiv2/hosts/')
                .then(async (res) => {
                    if (res.status !== 200) {
                        throw new Error();
                    }
                    return res.json();
                })
                .then((data) => {
                    data.sort((hostA, hostB) => {
                        return hostB.host_name < hostA.host_name;
                    });
                    return setHosts(data);
                });
        }
    }, [recipientMode, hosts.length]);

    const handleSend = () => {
        let formValid = true;
        setSelectHostInputError(null);

        if (title.value.trim() === '') {
            title.setError('This field cannot be empty');
            formValid = false;
        }

        if (url.value.trim() === '') {
            url.setError('This field cannot be empty');
            formValid = false;
        }

        if (validateURL(url.value.trim()) === false) {
            url.setError('Invalid URL');
            formValid = false;
        }

        if (recipientMode === 'SELECT' && selectedHosts.length === 0) {
            formValid = false;
            setSelectHostInputError('This field cannot be empty');
        }

        if (formValid === false) {
            return;
        }

        setLoading(true);

        // if user dont use the SELECT option we send null this way if user toggle back the SELECT option state will stay
        const selectedHostsData =
            recipientMode === 'SELECT' ? selectedHosts.map((host) => host.host_id) : null;

        createNotification(title.value.trim(), url.value.trim(), selectedHostsData)
            .then(() => notify.success('Notification Sent!'))
            .catch((e) => notify.error(e))
            .finally(() => setLoading(false));
    };

    return (
        <AdminPage>
            <ControlBar>
                <ControlBar>
                    <ControlBarHeader>Notification Centre</ControlBarHeader>
                </ControlBar>
            </ControlBar>

            <Grid container spacing={4}>
                <Grid item xs={12}>
                    <Section>
                        <Header>Recipients</Header>
                        <Typography>Choose who will receive this notification</Typography>

                        <Content>
                            <RadioGroup
                                aria-label="Recipients"
                                value={recipientMode}
                                onChange={(e) => setRecipientMode(e.target.value)}
                            >
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    mb={2}
                                    alignItems="flex-start"
                                >
                                    <FormControlLabel
                                        value={'ALL'}
                                        control={<Radio />}
                                        label="All Hosts"
                                    />
                                    <SwitchSubHeader>
                                        This notification will be made to all hosts in the system
                                    </SwitchSubHeader>
                                </Box>
                                <Box display="flex" flexDirection="column" alignItems="flex-start">
                                    <FormControlLabel
                                        value={'SELECT'}
                                        control={<Radio />}
                                        label={'Specific Hosts Only'}
                                    />
                                </Box>
                            </RadioGroup>
                        </Content>
                    </Section>
                </Grid>
                <Grid item xs={12}>
                    <Section>
                        <Header>Notification</Header>
                        <Typography>Title and link that will appear in the users inbox</Typography>

                        <Content>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={7}>
                                    <TextField label="Title" {...title.formProps} />
                                </Grid>
                                <Grid item xs={12} md={7}>
                                    <TextField label="URL" {...url.formProps} />
                                </Grid>
                                {recipientMode === 'SELECT' && (
                                    <Grid item xs={12} md={7}>
                                        <FormControl
                                            variant="outlined"
                                            style={{ width: '100%' }}
                                            error={!!selectHostInputError}
                                        >
                                            <Autocomplete
                                                multiple
                                                id="selectedHosts"
                                                onChange={(_, value) => {
                                                    setSelectedHosts(value);
                                                }}
                                                filterSelectedOptions
                                                options={hosts}
                                                getOptionLabel={(host) => host.host_name}
                                                renderTags={(value, getTagProps) =>
                                                    value.map((option, index) => (
                                                        <Chip
                                                            size="small"
                                                            variant="outlined"
                                                            label={option.host_name}
                                                            {...getTagProps({ index })}
                                                        />
                                                    ))
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        error={!!selectHostInputError}
                                                        variant="outlined"
                                                        label="Select Hosts"
                                                        placeholder="Select Hosts"
                                                    />
                                                )}
                                            />
                                            {selectHostInputError && (
                                                <FormHelperText>
                                                    {selectHostInputError}
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>
                                )}
                            </Grid>
                        </Content>
                    </Section>
                </Grid>
            </Grid>

            <Box display="flex" justifyContent="flex-end" mt={4}>
                <SubmitButton onClick={handleSend} disabled={loading}>
                    Send
                </SubmitButton>
            </Box>
        </AdminPage>
    );
}

const SwitchSubHeader = styled(Typography)(({ theme }) => ({
    marginLeft: 31,
    marginTop: -6,
}));
SwitchSubHeader.defaultProps = { variant: 'caption' };
